import {
  type QueryClient,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { ApiError, api, query } from "./api";
import { useLive } from "./live";
import type {
  AiStatus,
  CaseCard,
  CaseStatus,
  CaseSummary,
  CompanyProfile,
  Contact,
  ContactCard,
  ConversationDetail,
  ConversationStatus,
  ConversationSummary,
  Invitation,
  LumiConfig,
  Me,
  Message,
  NotificationItem,
  Organization,
  PartRequestCard,
  PartRequestStatus,
  PartRequestSummary,
  QuickReply,
  QuoteCard,
  QuoteLine,
  QuoteSettings,
  QuoteSummary,
  SalesPoint,
  TeamArea,
  TeamMember,
  TeamSettings,
  Template,
} from "./types";

/** With the live socket connected the inbox barely polls; without it, it polls every 5 s. */
const pollMs = (live: boolean) => (live ? 60_000 : 5_000);

export const keys = {
  me: ["me"] as const,
  team: ["team"] as const,
  conversations: (filter: string) => ["conversations", filter] as const,
  conversation: (id: string) => ["conversation", id] as const,
  messages: (id: string) => ["messages", id] as const,
  contacts: (q: string) => ["contacts", q] as const,
  contact: (id: string) => ["contact", id] as const,
  organizations: ["organizations"] as const,
  quickReplies: ["quick-replies"] as const,
  company: ["company"] as const,
  ai: ["ai"] as const,
  lumiConfig: ["ai", "config"] as const,
  /** Under "ai", so the switch and the setup refresh it; products, knowledge and company too (live.tsx). */
  firstSteps: ["ai", "first-steps"] as const,
  products: (q: string, category: string) => ["products", "list", q, category] as const,
  product: (id: string) => ["products", "one", id] as const,
  productCategories: ["products", "categories"] as const,
  knowledgeList: (q: string, kind: string) => ["knowledge", "list", q, kind] as const,
  knowledge: (id: string) => ["knowledge", "one", id] as const,
  salesPoints: (q: string, province: string) => ["sales-points", "list", q, province] as const,
  salesPointProvinces: ["sales-points", "provinces"] as const,
  templates: (sendable: boolean) => ["templates", sendable] as const,
  cases: (filter: CaseFilter) => ["cases", "list", filter] as const,
  case: (id: string) => ["cases", "one", id] as const,
  caseStatuses: ["cases", "statuses"] as const,
  // Under "cases": part requests live-update with them.
  partRequests: (filter: PartRequestFilter) => ["cases", "parts", "list", filter] as const,
  partRequest: (id: string) => ["cases", "parts", "one", id] as const,
  partRequestStatuses: ["cases", "parts", "statuses"] as const,
  quotes: (filter: QuoteFilter) => ["quotes", "list", filter] as const,
  quote: (id: string) => ["quotes", "one", id] as const,
  quoteSettings: ["quotes", "settings"] as const,
  report: (from: string, to: string) => ["reports", from, to] as const,
};

export const useMe = () => useQuery({ queryKey: keys.me, queryFn: () => api.get<Me>("/me") });

const teamQuery = queryOptions({
  queryKey: keys.team,
  queryFn: () => api.get<{ items: TeamMember[] }>("/team"),
  staleTime: 60_000,
});
export const useTeam = () => useQuery(teamQuery);

const teamSettingsQuery = queryOptions({
  queryKey: [...keys.team, "settings"],
  queryFn: () => api.get<TeamSettings>("/team/settings"),
  staleTime: 5 * 60_000,
});
export const useTeamSettings = () => useQuery(teamSettingsQuery);

export function useSaveTeamSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TeamSettings) => api.put<TeamSettings>("/team/settings", input),
    onSuccess: (s) => qc.setQueryData(teamSettingsQuery.queryKey, s),
  });
}

const teamAreasQuery = queryOptions({
  queryKey: [...keys.team, "areas"],
  queryFn: async () => (await api.get<{ items: TeamArea[] }>("/team/areas")).items,
  staleTime: 5 * 60_000,
});
export const useTeamAreas = () => useQuery(teamAreasQuery);

export function useSaveTeamAreas() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: (Omit<TeamArea, "id"> & { id?: string })[]) =>
      api.put<{ items: TeamArea[] }>("/team/areas", { items }),
    onSuccess: (r) => qc.setQueryData(teamAreasQuery.queryKey, r.items),
  });
}

export const invitationsQuery = queryOptions({
  queryKey: ["invitations"],
  queryFn: async () => (await api.get<{ items: Invitation[] }>("/team/invitations")).items,
});

export type InboxFilter = "all" | "mine" | "unassigned" | "bot" | "closed";

const FILTER_QUERY: Record<InboxFilter, Record<string, string>> = {
  all: { status: "bot,human" },
  mine: { status: "bot,human", assignee: "me" },
  unassigned: { status: "bot,human", assignee: "unassigned" },
  bot: { status: "bot" },
  closed: { status: "closed" },
};

type ConversationPage = { items: ConversationSummary[]; nextBefore: number | null };

/** Open conversations newest first, 50 per page ("Ver más" loads the next one). */
export const useConversations = (filter: InboxFilter) => {
  const live = useLive();
  return useInfiniteQuery({
    queryKey: keys.conversations(filter),
    queryFn: ({ pageParam }) =>
      api.get<ConversationPage>(
        `/conversations${query({ ...FILTER_QUERY[filter], ...(pageParam ? { before: String(pageParam) } : {}) })}`,
      ),
    initialPageParam: 0,
    getNextPageParam: (last) => last.nextBefore ?? undefined,
    refetchInterval: pollMs(live),
  });
};

/**
 * Everyone waiting for a person, never cut by paging: the longest waits are the oldest
 * conversations, exactly the ones a newest-first page would leave out.
 */
export const useWaitingConversations = (filter: InboxFilter, enabled = true) => {
  const live = useLive();
  return useQuery({
    queryKey: [...keys.conversations(filter), "waiting"],
    queryFn: () =>
      api.get<ConversationPage>(`/conversations${query({ ...FILTER_QUERY[filter], waiting: "1", limit: "200" })}`),
    enabled: enabled && filter !== "closed",
    refetchInterval: pollMs(live),
  });
};

export const useConversation = (id: string | undefined) => {
  const live = useLive();
  return useQuery({
    queryKey: keys.conversation(id ?? ""),
    queryFn: () => api.get<ConversationDetail>(`/conversations/${id}`),
    enabled: !!id,
    refetchInterval: pollMs(live),
  });
};

export const useMessages = (id: string | undefined) => {
  const live = useLive();
  return useQuery({
    queryKey: keys.messages(id ?? ""),
    queryFn: async () =>
      (await api.get<{ items: Message[] }>(`/conversations/${id}/messages?limit=200`)).items.reverse(),
    enabled: !!id,
    refetchInterval: pollMs(live),
  });
};

const quickRepliesQuery = queryOptions({
  queryKey: keys.quickReplies,
  queryFn: async () => (await api.get<{ items: QuickReply[] }>("/quick-replies")).items,
  staleTime: 60_000,
});
export const useQuickReplies = () => useQuery(quickRepliesQuery);

const contactsQuery = (q: string) =>
  queryOptions({
    queryKey: keys.contacts(q),
    queryFn: () => api.get<{ items: Contact[]; nextOffset: number | null }>(`/contacts${query({ q, limit: 100 })}`),
  });
export const useContacts = (q: string) => useQuery(contactsQuery(q));

export const useContact = (id: string | undefined) =>
  useQuery({
    queryKey: keys.contact(id ?? ""),
    queryFn: () => api.get<ContactCard>(`/contacts/${id}`),
    enabled: !!id,
  });

type Product = import("./types").Product;
type ProductDetail = import("./types").ProductDetail;

type ProductFilter = { kind?: Product["kind"] | ""; withInactive?: boolean };

const productsQuery = (q: string, category: string, opts: ProductFilter = {}) =>
  queryOptions({
    queryKey: [...keys.products(q, category), opts.kind ?? "", !!opts.withInactive],
    queryFn: () =>
      api.get<{ items: Product[]; nextOffset: number | null }>(
        `/products${query({
          q: q || undefined,
          category: category || undefined,
          kind: opts.kind || undefined,
          status: opts.withInactive ? undefined : "active",
          limit: "500",
        })}`,
      ),
  });
export const useProducts = (q: string, category: string, opts: ProductFilter = {}) =>
  useQuery({ ...productsQuery(q, category, opts), placeholderData: (prev) => prev });

const productCategoriesQuery = queryOptions({
  queryKey: keys.productCategories,
  queryFn: async () => (await api.get<{ items: string[] }>("/products/categories")).items,
  staleTime: 60_000,
});
export const useProductCategories = () => useQuery(productCategoriesQuery);

export const useProduct = (id: string | undefined) =>
  useQuery({
    queryKey: keys.product(id ?? ""),
    queryFn: () => api.get<ProductDetail>(`/products/${id}`),
    enabled: !!id,
  });

export function useSaveProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: Partial<Product> }) =>
      id ? api.patch<Product>(`/products/${id}`, input) : api.post<Product>("/products", input),
    onSettled: () => qc.invalidateQueries({ queryKey: ["products"] }),
  });
}

/** Uploads a prepared photo; the product comes back with it at the end of its photos. */
export function useUploadProductPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, files }: { id: string; files: { full: Blob; thumb: Blob; wa: Blob } }) => {
      const form = new FormData();
      form.append("full", files.full, "full.webp");
      form.append("thumb", files.thumb, "thumb.webp");
      form.append("wa", files.wa, "wa.jpg");
      return api.form<Product>(`/products/${id}/images`, form);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["products"] }),
  });
}

export const useImproveDescription = () =>
  useMutation({
    mutationFn: (id: string) =>
      api.post<
        { status: "ok"; description: string; left: number } | { status: "daily_limit" | "failed"; left: number }
      >(`/products/${id}/improve-description`),
  });

export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/products/${id}`),
    onSettled: () => qc.invalidateQueries({ queryKey: ["products"] }),
  });
}

export function useImportFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: import("../features/products/csv").CsvProduct[]) =>
      api.post<{ found: number; created: number; updated: number }>("/products/import/file", { items }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["products"] }),
  });
}

export function useImportStore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (url: string) =>
      api.post<{ found: number; created: number; updated: number }>("/products/import/woocommerce", { url }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["products"] }),
  });
}

type KnowledgeDoc = import("./types").KnowledgeDoc;

const knowledgeListQuery = (q: string, kind: string) =>
  queryOptions({
    queryKey: keys.knowledgeList(q, kind),
    queryFn: () =>
      api.get<{ items: import("./types").KnowledgeListItem[] }>(
        `/knowledge${query({ q: q || undefined, kind: kind || undefined })}`,
      ),
  });
export const useKnowledgeList = (q: string, kind: string) =>
  useQuery({ ...knowledgeListQuery(q, kind), placeholderData: (prev) => prev });

export const useKnowledge = (id: string | undefined) =>
  useQuery({
    queryKey: keys.knowledge(id ?? ""),
    queryFn: () => api.get<KnowledgeDoc>(`/knowledge/${id}`),
    enabled: !!id,
  });

export interface KnowledgeSave {
  id?: string;
  input: Pick<KnowledgeDoc, "title" | "kind" | "body" | "productIds" | "visibleToAi">;
  /** Original file to attach (null removes the current one). */
  file?: { blob: File; type: string } | null;
}

/** Saves the text, then attaches or removes the original file. */
export function useSaveKnowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input, file }: KnowledgeSave) => {
      let doc = id
        ? await api.patch<KnowledgeDoc>(`/knowledge/${id}`, input)
        : await api.post<KnowledgeDoc>("/knowledge", input);
      if (file) doc = await api.upload<KnowledgeDoc>(`/knowledge/${doc.id}/file`, file.blob, file.blob.name, file.type);
      else if (file === null && doc.fileKey) {
        await api.delete(`/knowledge/${doc.id}/file`);
        doc = { ...doc, fileKey: null, fileName: null, fileType: null, fileSize: null };
      }
      return doc;
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["knowledge"] }),
  });
}

export const useSearchKnowledge = () =>
  useMutation({
    mutationFn: (query: string) =>
      api.post<{ mode: "semantic" | "words"; hits: import("./types").SearchHit[] }>("/search", { query, limit: 6 }),
  });

export function useDeleteKnowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/knowledge/${id}`),
    onSettled: () => qc.invalidateQueries({ queryKey: ["knowledge"] }),
  });
}

const salesPointsQuery = (q: string, province: string) =>
  queryOptions({
    queryKey: keys.salesPoints(q, province),
    queryFn: () =>
      api.get<{ items: SalesPoint[] }>(`/sales-points${query({ q: q || undefined, province: province || undefined })}`),
  });
export const useSalesPoints = (q: string, province: string) =>
  useQuery({ ...salesPointsQuery(q, province), placeholderData: (prev) => prev });

const salesPointProvincesQuery = queryOptions({
  queryKey: keys.salesPointProvinces,
  queryFn: async () => (await api.get<{ items: string[] }>("/sales-points/provinces")).items,
  staleTime: 60_000,
});
export const useSalesPointProvinces = () => useQuery(salesPointProvincesQuery);

export function useSaveSalesPoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: Partial<SalesPoint> }) =>
      id ? api.patch<SalesPoint>(`/sales-points/${id}`, input) : api.post<SalesPoint>("/sales-points", input),
    onSettled: () => qc.invalidateQueries({ queryKey: ["sales-points"] }),
  });
}

export function useDeleteSalesPoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/sales-points/${id}`),
    onSettled: () => qc.invalidateQueries({ queryKey: ["sales-points"] }),
  });
}

export function useImportSalesPoints() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: Partial<SalesPoint>[]) =>
      api.post<{ found: number; created: number; updated: number }>("/sales-points/import/file", { items }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["sales-points"] }),
  });
}

export interface ArenaModel {
  id: string;
  label: string;
  origin: string;
  tier: "barato" | "medio" | "caro";
}

export interface ArenaResult {
  slot: string;
  model: string;
  reply: string;
  handoff: boolean;
  tools: string[];
  costUsd: number;
  latencyMs: number;
  /** Contact data this model saved. */
  saved?: Record<string, string>;
  error?: string;
}

/** Null when the arena is off (production) or the user is not the owner. */
export const useArenaModels = () =>
  useQuery({
    queryKey: ["arena", "models"],
    queryFn: async () => {
      try {
        return await api.get<{ items: ArenaModel[]; defaults: string[] }>("/arena/models");
      } catch (err) {
        if (err instanceof ApiError && (err.status === 404 || err.status === 403)) return null;
        throw err;
      }
    },
    staleTime: 300_000,
  });

export const useArenaSummary = () =>
  useQuery({
    queryKey: ["arena", "summary"],
    queryFn: () =>
      api.get<{
        turns: number;
        sessions: number;
        spentUsd: number;
        models: {
          model: string;
          answered: number;
          voted: number;
          wins: number;
          winRate: number;
          flagged: number;
          errors: number;
          avgCostUsd: number;
          avgLatencyMs: number;
        }[];
      }>("/arena/summary"),
  });

export function useArenaTurn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      sessionId: string;
      history: { role: "user" | "assistant"; content: string }[];
      message: string;
      models: string[];
      contact?: Record<string, string>;
    }) => api.post<{ turnId: string; results: ArenaResult[] }>("/arena/turn", input),
    onSettled: () => qc.invalidateQueries({ queryKey: ["arena", "summary"] }),
  });
}

export function useArenaVote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ turnId, ...vote }: { turnId: string; best: string | null; flags: Record<string, string[]> }) =>
      api.post(`/arena/turn/${turnId}/vote`, vote),
    onSettled: () => qc.invalidateQueries({ queryKey: ["arena", "summary"] }),
  });
}

const companyQuery = queryOptions({
  queryKey: keys.company,
  queryFn: () => api.get<CompanyProfile>("/company"),
  staleTime: 5 * 60_000,
});
export const useCompany = () => useQuery(companyQuery);

export function useUpdateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      input: Partial<Record<"phone" | "address" | "hours" | "website" | "email" | "legalName" | "legalId", string>> & {
        schedule?: import("./types").CompanyProfile["schedule"];
      },
    ) => api.put<import("./types").CompanyProfile>("/company", input),
    onSuccess: (data) => qc.setQueryData(keys.company, data),
  });
}

export const useOrganizations = () =>
  useQuery({
    queryKey: keys.organizations,
    queryFn: async () => (await api.get<{ items: Organization[] }>("/organizations?limit=200")).items,
    staleTime: 60_000,
  });

/** Refreshes everything the inbox shows about one conversation. */
function useInvalidateConversation() {
  const qc = useQueryClient();
  return (id: string) => {
    qc.invalidateQueries({ queryKey: ["conversations"] });
    qc.invalidateQueries({ queryKey: keys.conversation(id) });
    qc.invalidateQueries({ queryKey: keys.messages(id) });
  };
}

export function useSendMessage(conversationId: string) {
  const invalidate = useInvalidateConversation();
  return useMutation({
    mutationFn: (body: string) => api.post<Message>(`/conversations/${conversationId}/messages`, { body }),
    onSettled: () => invalidate(conversationId),
  });
}

export function useAssign(conversationId: string) {
  const invalidate = useInvalidateConversation();
  return useMutation({
    mutationFn: (assigneeId: string | null) => api.post(`/conversations/${conversationId}/assign`, { assigneeId }),
    onSettled: () => invalidate(conversationId),
  });
}

export function useSetStatus(conversationId: string) {
  const invalidate = useInvalidateConversation();
  return useMutation({
    mutationFn: (status: ConversationStatus) => api.post(`/conversations/${conversationId}/status`, { status }),
    onSettled: () => invalidate(conversationId),
  });
}

export function useUpdateContact(contactId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<Contact>) => api.patch<Contact>(`/contacts/${contactId}`, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contacts"] });
      qc.invalidateQueries({ queryKey: keys.contact(contactId) });
      qc.invalidateQueries({ queryKey: ["conversation"] });
      qc.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
}

const templatesQuery = (sendableOnly: boolean) =>
  queryOptions({
    queryKey: keys.templates(sendableOnly),
    queryFn: async () => (await api.get<{ items: Template[] }>(`/templates${sendableOnly ? "?sendable=1" : ""}`)).items,
    staleTime: 5 * 60_000,
  });
export const useTemplates = (sendableOnly: boolean) => useQuery(templatesQuery(sendableOnly));

export function useSyncTemplates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ results: { wabaId: string; synced?: number; error?: string }[] }>("/templates/sync"),
    onSettled: () => qc.invalidateQueries({ queryKey: ["templates"] }),
  });
}

export function useUpdateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: string;
      hidden?: boolean;
      variableMap?: import("@orbis/shared").TemplateVariableMap;
    }) => api.patch(`/templates/${id}`, input),
    onSettled: () => qc.invalidateQueries({ queryKey: ["templates"] }),
  });
}

export function useSendTemplate(conversationId: string) {
  const invalidate = useInvalidateConversation();
  return useMutation({
    mutationFn: (input: {
      templateId: string;
      bodyValues: Record<string, string>;
      headerValues: Record<string, string>;
    }) => api.post<Message>(`/conversations/${conversationId}/template`, input),
    onSettled: () => invalidate(conversationId),
  });
}

/** Whether the IA answers the company's WhatsApp ("IA activa"). */
const aiStatusQuery = queryOptions({ queryKey: keys.ai, queryFn: () => api.get<AiStatus>("/ai") });
export const useAiStatus = () => useQuery(aiStatusQuery);

export type FirstStepFlags = Record<"setup" | "description" | "hours" | "catalog" | "knowledge" | "lumiOn", boolean>;
export const firstStepsQuery = queryOptions({
  queryKey: keys.firstSteps,
  queryFn: () => api.get<FirstStepFlags>("/ai/first-steps"),
  staleTime: 60_000,
});

export function useSetAiEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => api.put<{ enabled: boolean }>("/ai", { enabled }),
    onSuccess: (data) => {
      qc.setQueryData<AiStatus>(keys.ai, (old) => (old ? { ...old, ...data } : { ...data, balance: null }));
      // Turning it on the first time starts the trial: fetch the balance it set.
      qc.invalidateQueries({ queryKey: keys.ai });
    },
  });
}

/** Owners and admins only (the API answers 403 to agents): pass false to skip it. */
const lumiConfigQuery = queryOptions({ queryKey: keys.lumiConfig, queryFn: () => api.get<LumiConfig>("/ai/config") });
export const useLumiConfig = (enabled = true) => useQuery({ ...lumiConfigQuery, enabled });

// ---- Notifications ----------------------------------------------------------

export const notificationsQuery = queryOptions({
  queryKey: ["notifications"],
  queryFn: () => api.get<{ items: NotificationItem[]; unread: number }>("/notifications"),
});
export const useNotifications = () => useQuery(notificationsQuery);

export type NotificationRead = { ids: string[] } | { conversationId: string } | { all: true };

export function useMarkNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NotificationRead) => api.post<{ read: number }>("/notifications/read", input),
    onSuccess: (r) => {
      if (r.read) qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

// ---- After-sales cases ------------------------------------------------------

export interface CaseFilter {
  q?: string;
  state?: "open" | "closed" | "all";
  assignee?: string;
  statusId?: string;
  contactId?: string;
}

const casesQuery = (filter: CaseFilter) =>
  queryOptions({
    queryKey: keys.cases(filter),
    queryFn: async () =>
      (
        await api.get<{ items: CaseSummary[] }>(
          `/cases${query({
            q: filter.q || undefined,
            state: filter.state,
            assignee: filter.assignee || undefined,
            statusId: filter.statusId || undefined,
            contactId: filter.contactId,
          })}`,
        )
      ).items,
  });
export const useCases = (filter: CaseFilter, enabled = true) =>
  useQuery({ ...casesQuery(filter), enabled, placeholderData: (prev) => prev });

export const useCase = (id: string) =>
  useQuery({ queryKey: keys.case(id), queryFn: () => api.get<CaseCard>(`/cases/${id}`) });

const caseStatusesQuery = queryOptions({
  queryKey: keys.caseStatuses,
  queryFn: async () => (await api.get<{ items: CaseStatus[] }>("/cases/statuses")).items,
  staleTime: 5 * 60_000,
});
export const useCaseStatuses = () => useQuery(caseStatusesQuery);

export type CaseInput = Partial<
  Pick<
    CaseCard,
    | "kind"
    | "title"
    | "description"
    | "productId"
    | "model"
    | "serial"
    | "purchaseDate"
    | "purchasePlace"
    | "invoice"
    | "assigneeId"
    | "statusId"
  >
>;

export function useCreateCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CaseInput & { contactId: string; conversationId?: string | null; title: string }) =>
      api.post<CaseCard>("/cases", input),
    onSuccess: (c) => {
      qc.setQueryData(keys.case(c.id), c);
      qc.invalidateQueries({ queryKey: ["cases", "list"] });
    },
  });
}

export function useUpdateCase(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CaseInput) => api.patch<CaseCard>(`/cases/${id}`, input),
    onSuccess: (c) => {
      qc.setQueryData(keys.case(id), c);
      qc.invalidateQueries({ queryKey: ["cases", "list"] });
    },
  });
}

export function useAddCaseNote(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text: string) => api.post<CaseCard>(`/cases/${id}/notes`, { text }),
    onSuccess: (c) => qc.setQueryData(keys.case(id), c),
  });
}

export function useDeleteCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/cases/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cases", "list"] }),
  });
}

export function useSaveCaseStatuses() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: { id?: string; label: string; closed: boolean; notice: string | null }[]) =>
      api.put<{ items: CaseStatus[] }>("/cases/statuses", { items }),
    onSuccess: (r) => {
      qc.setQueryData(keys.caseStatuses, r.items);
      qc.invalidateQueries({ queryKey: ["cases"] });
    },
  });
}

// ---- Spare-part requests ------------------------------------------------------

export interface PartRequestFilter {
  q?: string;
  state?: "open" | "closed" | "all";
  assignee?: string;
  statusId?: string;
  contactId?: string;
  caseId?: string;
}

const partRequestsQuery = (filter: PartRequestFilter) =>
  queryOptions({
    queryKey: keys.partRequests(filter),
    queryFn: async () =>
      (
        await api.get<{ items: PartRequestSummary[] }>(
          `/part-requests${query({
            q: filter.q || undefined,
            state: filter.state,
            assignee: filter.assignee || undefined,
            statusId: filter.statusId || undefined,
            contactId: filter.contactId,
            caseId: filter.caseId,
          })}`,
        )
      ).items,
  });
export const usePartRequests = (filter: PartRequestFilter, enabled = true) =>
  useQuery({ ...partRequestsQuery(filter), enabled, placeholderData: (prev) => prev });

export const usePartRequest = (id: string) =>
  useQuery({ queryKey: keys.partRequest(id), queryFn: () => api.get<PartRequestCard>(`/part-requests/${id}`) });

const partRequestStatusesQuery = queryOptions({
  queryKey: keys.partRequestStatuses,
  queryFn: async () => (await api.get<{ items: PartRequestStatus[] }>("/part-requests/statuses")).items,
  staleTime: 5 * 60_000,
});
export const usePartRequestStatuses = () => useQuery(partRequestStatusesQuery);

export type PartRequestInput = Partial<
  Pick<
    PartRequestCard,
    | "partId"
    | "partName"
    | "productId"
    | "model"
    | "quantity"
    | "description"
    | "reference"
    | "caseId"
    | "assigneeId"
    | "statusId"
  >
>;

export function useCreatePartRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PartRequestInput & { contactId: string; conversationId?: string | null; partName: string }) =>
      api.post<PartRequestCard>("/part-requests", input),
    onSuccess: (r) => {
      qc.setQueryData(keys.partRequest(r.id), r);
      qc.invalidateQueries({ queryKey: ["cases", "parts", "list"] });
      if (r.caseId) qc.invalidateQueries({ queryKey: keys.case(r.caseId) });
    },
  });
}

export function useUpdatePartRequest(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PartRequestInput) => api.patch<PartRequestCard>(`/part-requests/${id}`, input),
    onSuccess: (r) => {
      qc.setQueryData(keys.partRequest(id), r);
      qc.invalidateQueries({ queryKey: ["cases", "parts", "list"] });
      if (r.caseId) qc.invalidateQueries({ queryKey: keys.case(r.caseId) });
    },
  });
}

export function useAddPartRequestNote(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text: string) => api.post<PartRequestCard>(`/part-requests/${id}/notes`, { text }),
    onSuccess: (r) => qc.setQueryData(keys.partRequest(id), r),
  });
}

export function useDeletePartRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/part-requests/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cases"] }),
  });
}

export function useSavePartRequestStatuses() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: { id?: string; label: string; closed: boolean; arrived: boolean; notice: string | null }[]) =>
      api.put<{ items: PartRequestStatus[] }>("/part-requests/statuses", { items }),
    onSuccess: (r) => {
      qc.setQueryData(keys.partRequestStatuses, r.items);
      qc.invalidateQueries({ queryKey: ["cases", "parts"] });
    },
  });
}

// ---- Quotes -------------------------------------------------------------------

export interface QuoteFilter {
  q?: string;
  state?: "open" | "draft" | "sent" | "expired" | "accepted" | "rejected" | "all";
  assignee?: string;
  contactId?: string;
}

const quotesQuery = (filter: QuoteFilter) =>
  queryOptions({
    queryKey: keys.quotes(filter),
    queryFn: async () =>
      (
        await api.get<{ items: QuoteSummary[] }>(
          `/quotes${query({
            q: filter.q || undefined,
            state: filter.state,
            assignee: filter.assignee || undefined,
            contactId: filter.contactId,
          })}`,
        )
      ).items,
  });
export const useQuotes = (filter: QuoteFilter, enabled = true) =>
  useQuery({ ...quotesQuery(filter), enabled, placeholderData: (prev) => prev });

export const useQuote = (id: string) =>
  useQuery({ queryKey: keys.quote(id), queryFn: () => api.get<QuoteCard>(`/quotes/${id}`) });

const quoteSettingsQuery = queryOptions({
  queryKey: keys.quoteSettings,
  queryFn: () => api.get<QuoteSettings>("/quotes/settings"),
  staleTime: 5 * 60_000,
});
export const useQuoteSettings = () => useQuery(quoteSettingsQuery);

export function useSaveQuoteSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<QuoteSettings>) => api.put<QuoteSettings>("/quotes/settings", input),
    onSuccess: (s) => qc.setQueryData(keys.quoteSettings, s),
  });
}

/** Uploads the letterhead (PDF, PNG or JPG), or removes it with null. */
export function useQuoteLetterhead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File | null) => {
      if (!file) return api.delete<QuoteSettings>("/quotes/settings/letterhead");
      const form = new FormData();
      form.append("file", file, file.name);
      return api.form<QuoteSettings>("/quotes/settings/letterhead", form);
    },
    onSuccess: (s) => qc.setQueryData(keys.quoteSettings, s),
  });
}

export type QuoteInput = Partial<
  Pick<QuoteCard, "currency" | "terms" | "reference" | "validUntil" | "assigneeId" | "status"> & { lines: QuoteLine[] }
>;

export function useCreateQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: QuoteInput & { contactId: string; conversationId?: string | null }) =>
      api.post<QuoteCard>("/quotes", input),
    onSuccess: (q) => {
      qc.setQueryData(keys.quote(q.id), q);
      qc.invalidateQueries({ queryKey: ["quotes", "list"] });
    },
  });
}

export function useUpdateQuote(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: QuoteInput) => api.patch<QuoteCard>(`/quotes/${id}`, input),
    onSuccess: (q) => {
      qc.setQueryData(keys.quote(id), q);
      qc.invalidateQueries({ queryKey: ["quotes", "list"] });
    },
  });
}

/** The PDF to the customer's WhatsApp; a draft becomes sent. */
export function useSendQuote(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ quote: QuoteCard | null }>(`/quotes/${id}/send`),
    onSuccess: (r) => {
      if (r.quote) qc.setQueryData(keys.quote(id), r.quote);
      qc.invalidateQueries({ queryKey: ["quotes", "list"] });
    },
  });
}

export function useDeleteQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/quotes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quotes", "list"] }),
  });
}

/** The management report for a period (Costa Rica days, both included). */
export const useReport = (from: string, to: string) =>
  useQuery({
    queryKey: keys.report(from, to),
    queryFn: () => api.get<import("./types").Report>(`/reports${query({ from, to })}`),
    placeholderData: (prev) => prev,
    staleTime: 60_000,
  });

export function prefetchQuotes(qc: QueryClient) {
  qc.prefetchQuery({ ...quotesQuery({ state: "open" }), staleTime: AHEAD });
  qc.prefetchQuery(quoteSettingsQuery);
}

/*
 * A section's data fetched ahead (on hovering its menu link or one of its tabs, or on
 * opening it), so neither the section nor its tabs show a loading state, not even the
 * first time. Live events still mark the
 * data stale, so a tab shows what was saved and refreshes when opened. Data with no
 * staleTime of its own counts as fresh for 30 s, so hovering back and forth does not refetch.
 */
const AHEAD = 30_000;

export function prefetchCompany(qc: QueryClient) {
  qc.prefetchQuery(companyQuery);
  qc.prefetchQuery({ ...lumiConfigQuery, staleTime: AHEAD });
  qc.prefetchQuery({ ...salesPointsQuery("", ""), staleTime: AHEAD });
  qc.prefetchQuery(salesPointProvincesQuery);
  qc.prefetchQuery(quoteSettingsQuery);
}

export function prefetchCases(qc: QueryClient) {
  qc.prefetchQuery({ ...casesQuery({ state: "open" }), staleTime: AHEAD });
  qc.prefetchQuery(caseStatusesQuery);
  qc.prefetchQuery({ ...partRequestsQuery({ state: "open" }), staleTime: AHEAD });
  qc.prefetchQuery(partRequestStatusesQuery);
}

export function prefetchContacts(qc: QueryClient) {
  qc.prefetchQuery({ ...contactsQuery(""), staleTime: AHEAD });
}

export function prefetchCatalog(qc: QueryClient) {
  qc.prefetchQuery({ ...productsQuery("", ""), staleTime: AHEAD });
  qc.prefetchQuery(productCategoriesQuery);
}

export function prefetchKnowledge(qc: QueryClient) {
  qc.prefetchQuery({ ...knowledgeListQuery("", ""), staleTime: AHEAD });
}

export function prefetchLumi(qc: QueryClient) {
  qc.prefetchQuery({ ...lumiConfigQuery, staleTime: AHEAD });
  qc.prefetchQuery({ ...aiStatusQuery, staleTime: AHEAD });
  qc.prefetchQuery(firstStepsQuery);
}

export function prefetchSettings(qc: QueryClient) {
  qc.prefetchQuery(teamQuery);
  qc.prefetchQuery(teamSettingsQuery);
  qc.prefetchQuery(teamAreasQuery);
  qc.prefetchQuery(templatesQuery(false));
  qc.prefetchQuery(quickRepliesQuery);
  // Pending invitations are only for owners and admins.
  const me = qc.getQueryData<Me>(keys.me);
  const role = me?.tenants.find((t) => t.tenantId === me.activeTenantId)?.role;
  if (role === "owner" || role === "admin") qc.prefetchQuery({ ...invitationsQuery, staleTime: AHEAD });
}

export function useUpdateLumiConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<LumiConfig>) => api.put<LumiConfig>("/ai/config", input),
    onSuccess: (data) => qc.setQueryData(keys.lumiConfig, data),
  });
}
